/**
 * Minimal shape of a navigation item this filter needs. `FuseNavigationItem` structurally
 * satisfies it, so the app passes its real navigation through unchanged while this helper stays
 * dependency-free and unit-testable in isolation.
 */
export interface FilterableNavItem {
    id?: string;
    type?: string;
    children?: FilterableNavItem[];
}

/**
 * Removes navigation items the member has no access to.
 *
 * An item is dropped when its `id` maps (via `permissionMap`) to a permission the member does not
 * hold (`granted`). Parent groups/collapsables left without any visible child are pruned too, so
 * empty sections never appear. Items without a mapped permission are always kept.
 *
 * Pure and stateless: callers decide the "green pass" bypass (owners/admins) before calling this.
 */
export function filterNavigationByPermissions(
    items: FilterableNavItem[],
    granted: readonly string[],
    permissionMap: { [navItemId: string]: string }
): FilterableNavItem[] {
    return (items || [])
        .map((item) => ({
            ...item,
            children: item.children
                ? filterNavigationByPermissions(item.children, granted, permissionMap)
                : item.children
        }))
        // Drop items whose required permission the member does not have
        .filter((item) => {
            const required = item.id ? permissionMap[item.id] : undefined;
            return !required || (granted || []).includes(required);
        })
        // Drop parent sections that lost all of their children through the filter above
        .filter((item) => !(
            (item.type === 'group' || item.type === 'collapsable') &&
            Array.isArray(item.children) &&
            item.children.length === 0
        ));
}
